import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const RouteValidator = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Define route patterns with more comprehensive validation
  const routeConfig = {
    protected: [
      { pattern: '/dashboard', exact: true },
      { pattern: '/profile', exact: false },
      { pattern: '/organization-settings', exact: true },
      { pattern: '/website-checks', exact: true },
      { pattern: '/llm-settings', exact: true },
      { pattern: '/team', exact: false },
      { pattern: '/evaluations', exact: false },
      { pattern: '/templates', exact: false },
      { pattern: '/model-comparisons', exact: false },
      { pattern: '/test-auth', exact: true }
    ],
    admin: [
      { pattern: '/admin', exact: false }
    ],
    publicOnly: [
      { pattern: '/login', exact: true },
      { pattern: '/register', exact: true }
    ],
    public: [
      { pattern: '/', exact: true },
      { pattern: '/home', exact: true },
      { pattern: '/blog', exact: false },
      { pattern: '/product', exact: false },
      { pattern: '/company', exact: false },
      { pattern: '/legal', exact: false },
      { pattern: '/waitlist', exact: true },
      { pattern: '/email/verification', exact: true },
      { pattern: '/newsletter', exact: false },
      { pattern: '/invite', exact: false }
    ]
  };

  // Helper function to check if a path matches a route pattern
  const matchesRoute = useCallback((path, routePattern) => {
    if (routePattern.exact) {
      return path === routePattern.pattern;
    }
    return path.startsWith(routePattern.pattern);
  }, []);

  // Helper function to determine route type
  const getRouteType = useCallback((path) => {
    if (routeConfig.admin.some(route => matchesRoute(path, route))) {
      return 'admin';
    }
    if (routeConfig.protected.some(route => matchesRoute(path, route))) {
      return 'protected';
    }
    if (routeConfig.public.some(route => matchesRoute(path, route))) {
      return 'public';
    }
    if (routeConfig.publicOnly.some(route => matchesRoute(path, route))) {
      return 'publicOnly';
    }
    return 'unknown';
  }, [matchesRoute, routeConfig.admin, routeConfig.protected, routeConfig.public, routeConfig.publicOnly]);

  useEffect(() => {
    // Don't validate while auth is loading
    if (loading) {
      setIsValidating(true);
      return;
    }

    setIsValidating(false);
    const currentPath = location.pathname;
    const routeType = getRouteType(currentPath);

    // Log route validation for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Route validation:', {
        path: currentPath,
        type: routeType,
        user: user ? { id: user.id, is_admin: user.is_admin } : null
      });
    }

    // Handle different route types
    switch (routeType) {
      case 'protected':
        if (!user) {
          setValidationError({
            type: 'authentication_required',
            message: 'You must be logged in to access this page.',
            redirectTo: '/login',
            redirectState: { from: currentPath }
          });
          return;
        }
        break;

      case 'admin':
        if (!user) {
          setValidationError({
            type: 'authentication_required',
            message: 'You must be logged in to access this page.',
            redirectTo: '/login',
            redirectState: { from: currentPath }
          });
          return;
        }
        if (!user.is_admin) {
          setValidationError({
            type: 'admin_required',
            message: 'You must be an administrator to access this page.',
            redirectTo: '/dashboard'
          });
          return;
        }
        break;

      case 'publicOnly':
        if (user) {
          // Immediately redirect to dashboard without showing error screen
          navigate('/dashboard', { replace: true });
          return;
        }
        break;

      case 'public':
        // Public routes are always accessible
        break;

      case 'unknown':
        // Let unknown routes fall through to 404 handling
        if (process.env.NODE_ENV === 'development') {
          console.warn('Unknown route detected:', currentPath);
        }
        break;

      default:
        break;
    }

    // Clear any previous validation errors
    setValidationError(null);
  }, [location.pathname, user, loading, getRouteType, navigate]);

  // Handle validation errors
  useEffect(() => {
    if (validationError) {
      const timer = setTimeout(() => {
        if (validationError.redirectState) {
          navigate(validationError.redirectTo, {
            state: validationError.redirectState,
            replace: true
          });
        } else {
          navigate(validationError.redirectTo, { replace: true });
        }
      }, 2000); // Give user time to read the message

      return () => clearTimeout(timer);
    }
  }, [validationError, navigate]);

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Validating access...</p>
        </div>
      </div>
    );
  }

  // Show validation error message
  if (validationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto h-12 w-12 text-yellow-500">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Access Restricted
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {validationError.message}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Redirecting you in a moment...
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <button
              onClick={() => {
                if (validationError.redirectState) {
                  navigate(validationError.redirectTo, {
                    state: validationError.redirectState,
                    replace: true
                  });
                } else {
                  navigate(validationError.redirectTo, { replace: true });
                }
              }}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Continue
            </button>

            {validationError.type === 'authentication_required' && (
              <p className="text-xs text-gray-500">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/register', { replace: true })}
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  Sign up here
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default RouteValidator;
